import React, { useEffect, useLayoutEffect, useRef, useState } from "react"

import { navigate } from "gatsby"

import { Icon } from "@components/icon"
import { CONTENT_TYPES, EVENT_TYPES } from "@constants/recommender-system"
import SearchWrapper from "@layout/header/search-bar/search-wrapper"
import { sendLogToRecommender } from "@utils/recommender"
import { isValidArray } from "@utils/variables.helpers"

import { Flyout, flyoutLeft, flyoutRows } from "./flyout"

import * as styles from "./navbar-desktop.module.css"

// Grace period before a flyout closes, so a pointer cutting diagonally from a
// top link to the flyout doesn't drop it on the way.
const CLOSE_DELAY_MS = 200

// Keep in sync with --subcolumn-width in flyout.module.css.
const subcolumnWidth = () =>
  Math.min(353, Math.max(260, window.innerWidth * 0.245139))

const flyoutId = (navigationItem) => `nav-flyout-${navigationItem.id}`

// useLayoutEffect warns during SSR; the flyout is only ever measured in the
// browser, where it is safe.
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect

/**
 * The desktop category bar. Categories open a two-column flyout (the winning
 * V2 of the "D - SW - Nav Optimization" test, sc-1990) rather than the old
 * full-width dropdown. Mobile has its own navigation and is not affected.
 */
const NavbarDesktop = ({ mainLinks, menuLinks, location }) => {
  const [openId, setOpenId] = useState(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const navRef = useRef(null)
  const linkRefs = useRef({})
  const flyoutRefs = useRef({})
  const closeTimer = useRef(null)
  // Escape hands focus back to the top link; that focus must not reopen it.
  const suppressFocusOpen = useRef(false)

  const cancelClose = () => clearTimeout(closeTimer.current)

  const close = () => {
    cancelClose()
    setOpenId(null)
  }

  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpenId(null), CLOSE_DELAY_MS)
  }

  const openFlyout = (navigationItem) => {
    cancelClose()
    setOpenId(navigationItem.id)
  }

  const onTopLinkFocus = (navigationItem) => {
    if (suppressFocusOpen.current) {
      suppressFocusOpen.current = false
      return
    }
    openFlyout(navigationItem)
  }

  const onItemBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      scheduleClose()
    }
  }

  const onCategoryClick = (category) => {
    if (category && category.navigationId) {
      sendLogToRecommender({
        event: EVENT_TYPES.NAVBAR_CATEGORY,
        content_id: category.navigationId,
        content_type: CONTENT_TYPES.CATEGORY,
      })
    }
    if (category.link) {
      navigate(category.link)
    }
    close()
  }

  // Sit the flyout just below the category bar, under the link that opened
  // it, without letting it run off either edge of the viewport.
  useIsomorphicLayoutEffect(() => {
    if (openId === null) {
      return undefined
    }

    const place = () => {
      const nav = navRef.current
      const link = linkRefs.current[openId]
      const flyout = flyoutRefs.current[openId]
      if (!nav || !link || !flyout) {
        return
      }
      // clientWidth, not innerWidth: the flyout must stay clear of the
      // page's scrollbar.
      const viewportWidth = document.documentElement.clientWidth
      // The flyout's own width less the room it makes for an open
      // subcategory column, i.e. the first column.
      const flyoutStyle = window.getComputedStyle(flyout)
      const columnWidth =
        flyout.getBoundingClientRect().width -
        parseFloat(flyoutStyle.paddingLeft) -
        parseFloat(flyoutStyle.paddingRight)
      const openItem = mainLinks.find((item) => item.id === openId)
      const hasSubcolumn = flyoutRows(openItem).some((row) =>
        isValidArray(row.children)
      )
      const linkRect = link.getBoundingClientRect()
      const left = flyoutLeft({
        linkLeft: linkRect.left,
        linkRight: linkRect.right,
        columnWidth,
        subcolumnWidth: hasSubcolumn ? subcolumnWidth() : 0,
        viewportWidth,
      })
      setPosition({ top: nav.getBoundingClientRect().bottom, left })
    }

    place()
    window.addEventListener("scroll", place, { passive: true })
    window.addEventListener("resize", place)
    return () => {
      window.removeEventListener("scroll", place)
      window.removeEventListener("resize", place)
    }
  }, [openId])

  useEffect(() => {
    if (openId === null) {
      return undefined
    }
    const onKeyDown = (event) => {
      if (event.key !== "Escape") {
        return
      }
      const link = linkRefs.current[openId]
      close()
      if (link) {
        suppressFocusOpen.current = true
        link.focus()
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [openId])

  useEffect(() => cancelClose, [])

  return (
    <>
      <SearchWrapper menuLinks={menuLinks} location={location} />
      <nav className={styles.navbar} ref={navRef}>
        <ul className={styles.menu}>
          {mainLinks.map((navigationItem, index) => {
            const customStyle = {}
            let isButton = false
            if (navigationItem.backgroundColor) {
              customStyle.backgroundColor = navigationItem.backgroundColor
              isButton = true
            }
            if (navigationItem.color) {
              customStyle.color = navigationItem.color
            }
            const hasFlyout = isValidArray(navigationItem.children)
            const isOpen = openId === navigationItem.id
            return (
              <li
                key={index}
                className={`${styles.item} ${isButton ? styles.buttonItem : ""}`}
                onMouseEnter={
                  hasFlyout ? () => openFlyout(navigationItem) : undefined
                }
                onMouseLeave={hasFlyout ? scheduleClose : undefined}
                onBlur={hasFlyout ? onItemBlur : undefined}
              >
                <a
                  href={navigationItem.link}
                  className={styles.linkWrapper}
                  ref={(el) => {
                    linkRefs.current[navigationItem.id] = el
                  }}
                  aria-expanded={hasFlyout ? isOpen : undefined}
                  aria-controls={hasFlyout ? flyoutId(navigationItem) : undefined}
                  onFocus={
                    hasFlyout ? () => onTopLinkFocus(navigationItem) : undefined
                  }
                >
                  <div
                    className={styles.link}
                    onClick={() => onCategoryClick(navigationItem)}
                  >
                    <div
                      style={customStyle}
                      className={isButton ? "btn " + styles.navBtn : ""}
                    >
                      {navigationItem.name}
                    </div>
                    {hasFlyout && !isButton && (
                      <Icon
                        id="chevron-down"
                        className={styles.chevron}
                        aria-hidden="true"
                        focusable="false"
                      />
                    )}
                  </div>
                </a>
                {hasFlyout && (
                  <Flyout
                    id={flyoutId(navigationItem)}
                    ref={(el) => {
                      flyoutRefs.current[navigationItem.id] = el
                    }}
                    rows={flyoutRows(navigationItem)}
                    open={isOpen}
                    position={position}
                    onCategoryClick={onCategoryClick}
                  />
                )}
              </li>
            )
          })}
        </ul>
      </nav>
      <div
        className={styles.backdrop}
        style={{ top: position.top }}
        hidden={openId === null}
        aria-hidden="true"
      />
    </>
  )
}

export default NavbarDesktop
